import { ActionsPlanTypes } from 'legacy/lut/classes/helpers/partner360/usePartnerInfo'
import ValidationHelper from 'legacy/lut/classes/helpers/partner360/ValidationHelper'
import { filterBusinessPriorities } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/businessPriorities/helpers'
import {
  getActionableDescriptionQualityReasons,
  isDescriptionQualityBlocking,
} from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/descriptionQuality/helpers'
import { useDescriptionQuality } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/hooks/useDescriptionQuality'
import { useIncremental } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/hooks/useIncremental'
import { useResetProgressOnPracticeDevelopmentExit } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/hooks/useResetProgressOnPracticeDevelopmentExit'
import { KeyInitiativeInitTypes } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/models/keyInitiativeInitTypes'
import { ApplyButton } from 'legacy/lut/components/widgets/public/partner360/editors/ApplyButton'
import { CancelButton } from 'legacy/lut/components/widgets/public/partner360/editors/CancelButton'
import { reviewKeyInitiative } from 'legacy/lut/features/partner360/configuration'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  resetAllModifiedDataByKey,
  updateFormDataField,
  updateInvalidData,
  useGetFormData,
} from 'cng-common/features/form'
import { TFormValidationResult } from 'cng-common/features/form/models'
import { FormValidationBuilder } from 'cng-common/features/form/utils/validation'
import { useSafetyTranslation } from 'cng-common/features/translations'
import Logger from 'core/utils/Logger'
import {
  hasKeyInitiativeBusinessPriorities,
  isKeyInitiativeBusinessPrioritiesRequired,
  isKeyInitiativeBusinessPrioritiesVisible,
} from 'partner360/features/keyInitiativeEditor/lib/keyInitiativeBusinessPriorities'
import { isMdfStrategicInitiative } from 'partner360/features/keyInitiativeEditor/lib/keyInitiativeMdfFunding'
import { parseKeyInitiativeFocusArea } from 'partner360/features/keyInitiativeEditor/lib/parseKeyInitiativeFocusArea'
import { INITIATIVES_EDITOR_TYPE_STEPS } from 'partner360/features/keyInitiativeEditor/model/editorConfig'
import { useKeyInitiativePartnerMarket } from 'partner360/features/keyInitiativeEditor/model/hooks/useKeyInitiativePartnerMarket'
import { useKeyInitiativeProgramType } from 'partner360/features/keyInitiativeEditor/model/hooks/useKeyInitiativeProgramType'
import { TRANSLATION_KEYS as KEY_INITIATIVES_TRANSLATION_KEYS } from 'partner360/features/keyInitiatives/configs/translations'

import { TRANSLATION_KEYS as tr } from '../../../../../translations'
import {
  IS_INCREMENTAL_MODE_LOCKED_BY_FUNDING_SOURCE,
  IS_VALIDATE_ON_OPENING,
  PRACTICE_DEVELOPMENT,
} from '../../configs'
import { finSobValidation } from '../../helpers/finSobValidation'
import { getIsPracticeDevelopment } from '../../helpers/getPracticeDevelopmentProgressValues'
import {
  EKeyInitiativeGoalMode,
  isIncrementalForcedByInvestmentSource,
  shouldSetIncrementalForFunding,
} from '../../helpers/keyInitiativeGoalMode'
import { validateKeyInitiativeEstimateSales } from '../../helpers/validateKeyInitiativeEstimateSales'
import { validateKeyInitiativeFundingAndStatus } from '../../helpers/validateKeyInitiativeFundingAndStatus'
import { IKeyInitiativeEditorContentProps } from '../../models/IKeyInitiativeEditorContentProps'
import { IKeyInitiativeForm } from '../../models/keyInitiativeForm'
import { WidgetView } from '../content/widgetView/WidgetView'
import {
  FOUNDING_STEP_INDEX,
  KEY_INITIATIVES_STEPS,
  TMenuStepWithStatus,
} from '../keyInitiativeStepsContent/config'

import { KeyInitiativeStepsMenu } from './components/keyInitiativeStepsMenu/KeyInitiativeStepsMenu'
import { FoundingSection, GoalSection, InitiativeSection, StatusSection } from './sections'

import styles from './KeyInitiativeStepsContent.module.scss'

const areNumberSetsEqual = (left: Set<number>, right: Set<number>): boolean => {
  if (left.size !== right.size) {
    return false
  }

  return Array.from(left).every((value) => right.has(value))
}

const areFormValidationResultsEqual = (
  left: TFormValidationResult[],
  right: TFormValidationResult[]
): boolean =>
  left.length === right.length &&
  left.every(
    (leftResult, index) =>
      leftResult.field === right[index]?.field && leftResult.message === right[index]?.message
  )

export const KeyInitiativeStepsContent = (props: IKeyInitiativeEditorContentProps) => {
  const {
    keyInitiativesFormat = INITIATIVES_EDITOR_TYPE_STEPS,
    setKeyInitiativesFormat,
    keyObjectives,
    initiativeFormKey,
    initialData,
    pendingKeyInitiative,
    onApply,
    onBusyStateChange,
    finSobData,
    otherInitiatives,
    businessPriorities,
    partnerId,
    programType,
    fYear,
  } = props
  const shouldValidateOnOpening =
    IS_VALIDATE_ON_OPENING || Boolean(props.saveAsNew) || initialData?.entryId != null

  /** -------------------- State ----------------------- */

  const [currentStep, setCurrentStep] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const submissionInProgressRef = useRef(false)
  const [isGoalError, setIsGoalError] = useState(false)
  const [formValidationErrors, setFormValidationErrors] = useState<TFormValidationResult[]>([])
  const [stepsWithErrors, setStepsWithErrors] = useState<Set<number>>(
    () => new Set(shouldValidateOnOpening ? [0, 1, 2, 3] : [])
  )

  /** -------------------- Hooks ----------------------- */

  const dispatch = useAppDispatch()
  const t = useSafetyTranslation()
  const [{ formData, modifiedFields }] = useGetFormData(initiativeFormKey, initialData)
  useKeyInitiativeProgramType({
    formKey: initiativeFormKey,
    keyObjective: formData?.keyObjective,
    keyObjectives,
    programType,
    currentProgramType: formData?.programType,
  })
  const isEditingImportedInitiative = useMemo(
    () =>
      !props.saveAsNew &&
      initialData?.entryId != null &&
      formData?.initType === KeyInitiativeInitTypes.ImportedInitiative,
    [props.saveAsNew, initialData?.entryId, formData?.initType]
  )
  const partnerMarket = useKeyInitiativePartnerMarket(partnerId, fYear)
  const availableBusinessPriorities = useMemo(
    () =>
      filterBusinessPriorities({
        priorities: businessPriorities,
        fYear,
        programType,
        market: partnerMarket,
      }),
    [businessPriorities, fYear, programType, partnerMarket]
  )
  const isBusinessPrioritiesPresent = useMemo(
    () =>
      isKeyInitiativeBusinessPrioritiesVisible(
        availableBusinessPriorities,
        initialData?.businessPriorities
      ),
    [availableBusinessPriorities, initialData?.businessPriorities]
  )
  const isBusinessPrioritiesRequired = useMemo(
    () =>
      isKeyInitiativeBusinessPrioritiesRequired(
        formData?.keyObjective,
        keyObjectives,
        isBusinessPrioritiesPresent && !formData?.isNotApplicable
      ),
    [formData?.keyObjective, formData?.isNotApplicable, keyObjectives, isBusinessPrioritiesPresent]
  )
  const { incrementalFieldNameByValueType } = useIncremental<IKeyInitiativeForm>({
    initialData,
    formData,
  })
  const {
    checkDescriptionQuality,
    handleDescriptionBlur,
    isDescriptionQualityValidationActive,
    isDescriptionQualityBlocking: isDescriptionQualitySaveBlocked,
    isDescriptionQualityChecking,
  } = useDescriptionQuality({
    formKey: initiativeFormKey,
    name: formData?.name,
    description: formData?.description,
    quality: formData?.descriptionQuality,
    validateOnOpening: shouldValidateOnOpening && !isEditingImportedInitiative,
  })
  const isPracticeDevelopmentMountedRef = useRef(false)
  const modifiedFieldsRef = useRef(modifiedFields)
  const previousInvestmentSourceRef = useRef(formData?.hpInvestmentSource)
  const previousKeyObjectiveRef = useRef(formData?.keyObjective)
  const previousDefaultsKeyObjectiveRef = useRef(formData?.keyObjective)
  modifiedFieldsRef.current = modifiedFields
  const isPracticeDevelopment = useMemo(() => {
    return getIsPracticeDevelopment(
      formData?.keyObjective,
      keyObjectives,
      formData?.isPracticeDevelopment
    )
  }, [keyObjectives, formData?.keyObjective, formData?.isPracticeDevelopment])
  useResetProgressOnPracticeDevelopmentExit({ initiativeFormKey, isPracticeDevelopment })
  const isMdfKeyObjective = useMemo(
    () => isMdfStrategicInitiative(formData?.keyObjective, keyObjectives),
    [formData?.keyObjective, keyObjectives]
  )

  /** -------------------- Variables ----------------------- */

  const isBusy = isLoading || isDescriptionQualityChecking

  const disabledSteps = useMemo(
    () => new Set(isPracticeDevelopment ? [FOUNDING_STEP_INDEX] : []),
    [isPracticeDevelopment]
  )

  const menuSteps = useMemo<TMenuStepWithStatus[]>(
    () =>
      KEY_INITIATIVES_STEPS.map((step, index) => ({
        ...step,
        status: index < currentStep ? 'complete' : index === currentStep ? 'current' : 'incomplete',
      })),
    [currentStep]
  )

  const goalFieldName = useMemo(() => `goal${formData?.valueType}`, [formData?.valueType])
  const isGoalModeReadOnly =
    IS_INCREMENTAL_MODE_LOCKED_BY_FUNDING_SOURCE &&
    isIncrementalForcedByInvestmentSource(formData?.hpInvestmentSource)
  const goalMode = isGoalModeReadOnly
    ? EKeyInitiativeGoalMode.Incremental
    : (formData?.goalMode ?? initialData?.goalMode ?? EKeyInitiativeGoalMode.RunRate)
  const isIncrementalMode = goalMode === EKeyInitiativeGoalMode.Incremental
  const estimateSalesTargetField = isIncrementalMode
    ? incrementalFieldNameByValueType
    : goalFieldName
  const hasEstimateSalesValidationError = formValidationErrors.some(
    ({ field }) => field === estimateSalesTargetField
  )
  const runRateEstimateSalesValue =
    formData?.[goalFieldName] ?? initialData?.[goalFieldName] ?? null

  const isInvestmentNeeds = useMemo(
    () =>
      formData?.isInvestmentNeeds ??
      [initialData?.hpInvestmentSource, initialData?.hpInvestmentSum].some(Boolean),
    [formData?.isInvestmentNeeds, initialData?.hpInvestmentSource, initialData?.hpInvestmentSum]
  )
  const previousIsInvestmentNeedsRef = useRef(isInvestmentNeeds)

  const applyBlockingReasons = useMemo(() => {
    const reasons: string[] = []

    if (stepsWithErrors.size > 0) {
      const invalidSteps = Array.from(stepsWithErrors).map((stepIndex) => {
        const step = KEY_INITIATIVES_STEPS[stepIndex]

        return step ? `${stepIndex}: ${step.key}` : String(stepIndex)
      })
      const invalidFields = formValidationErrors.map(
        ({ field, message }) => `${String(field)}: ${message}`
      )
      reasons.push(
        `validation errors on tabs [${invalidSteps.join(', ')}]${invalidFields.length > 0 ? `; fields [${invalidFields.join(', ')}]` : ''}`
      )
    }

    if (isLoading) {
      reasons.push('initiative is being saved')
    }

    if (isDescriptionQualityChecking) {
      reasons.push('Description Quality check is in progress')
    }

    if (!isEditingImportedInitiative && isDescriptionQualitySaveBlocked) {
      const descriptionQuality = formData?.descriptionQuality
      const actionableReasons = getActionableDescriptionQualityReasons(
        descriptionQuality?.reasons ?? []
      )
      reasons.push(
        `Description Quality is blocking (score: ${descriptionQuality?.score ?? 'n/a'}, actionable reasons: [${actionableReasons.join(', ')}])`
      )
    }

    if (isGoalError && !formData?.justification) {
      reasons.push('FinSOB Goal validation requires Justification')
    }

    return reasons
  }, [
    stepsWithErrors,
    formValidationErrors,
    isLoading,
    isDescriptionQualityChecking,
    isDescriptionQualitySaveBlocked,
    isEditingImportedInitiative,
    isGoalError,
    formData?.descriptionQuality,
    formData?.justification,
  ])

  const isApplyDisabled = applyBlockingReasons.length > 0

  /** -------------------- Methods ----------------------- */

  const handleDescriptionValidate = useCallback(
    async (value: string): Promise<string> => {
      // Imported text is read-only; editing Name/BU/Funding must not require rewriting it.
      if (isEditingImportedInitiative) return ''

      if (isDescriptionQualitySaveBlocked) {
        return t(tr.keyInitiativeEditor.descriptionQuality.validation)
      }

      if (
        formData?.initType === KeyInitiativeInitTypes.ImportedInitiative ||
        initialData?.isFromGlobalPartner
      ) {
        return ''
      }

      return ValidationHelper.checkString(value)
    },
    [
      isEditingImportedInitiative,
      isDescriptionQualitySaveBlocked,
      t,
      formData?.initType,
      initialData?.isFromGlobalPartner,
    ]
  )

  const handleValidate = useCallback(async () => {
    const formValidation = FormValidationBuilder.build(initiativeFormKey)
    // Keep MDF/visibility validation in sync with
    // partner360/features/keyInitiatives/copyFromPreviousYear/lib/validateKeyInitiativeForCopy.ts.
    const businessPrioritiesResult: TFormValidationResult | undefined =
      isBusinessPrioritiesRequired &&
      !hasKeyInitiativeBusinessPriorities(formData?.businessPriorities)
        ? {
            field: 'businessPriorities',
            message: t(KEY_INITIATIVES_TRANSLATION_KEYS.businessPrioritiesRequired),
          }
        : undefined
    const { fundingResults, statusResults } = await validateKeyInitiativeFundingAndStatus({
      form: formData,
      isInvestmentNeeds,
      investmentSumRequiredMessage: t(
        tr.keyInitiativeEditor.investmentFieldGroup.investmentSumPositiveRequired
      ),
      isFundingDisabled: Boolean(initialData?.isFromGlobalPartner),
      isStatusDisabled: formData?.initType === KeyInitiativeInitTypes.ImportedInitiative,
    })

    const [step0Results, step1Results, step2Results, step3Results] = await Promise.all([
      // Step 0: InitiativeSection
      Promise.all(
        [
          formValidation.call(
            'name',
            isPracticeDevelopment || initialData?.isFromGlobalPartner
              ? PRACTICE_DEVELOPMENT
              : formData?.name
          ),
          !isEditingImportedInitiative &&
            keyObjectives.length &&
            formValidation.call('keyObjective', formData?.keyObjective),
          !isEditingImportedInitiative && formValidation.call('period', formData?.period),
          // Validate from current form state even while the Initiative tab is unmounted.
          handleDescriptionValidate(formData?.description ?? '').then((message) =>
            message ? { field: 'description', message } : undefined
          ),
          Promise.resolve(businessPrioritiesResult),
        ].filter(Boolean) as Promise<TFormValidationResult | undefined>[]
      ),
      // Step 1: FoundingSection
      Promise.resolve(fundingResults),
      // Step 2: GoalSection
      Promise.resolve([
        validateKeyInitiativeEstimateSales({
          field: estimateSalesTargetField,
          value: formData?.[estimateSalesTargetField],
          isRequired:
            !isEditingImportedInitiative && goalMode !== EKeyInitiativeGoalMode.NoSalesTarget,
        }),
      ]),
      // Step 3: StatusSection
      Promise.resolve(statusResults),
    ])

    const hasErrors = (results: (TFormValidationResult | undefined)[]) =>
      results.some((r) => r !== undefined)

    const nextStepsWithErrors = new Set([
      ...(hasErrors(step0Results) ? [0] : []),
      ...(hasErrors(step1Results) ? [1] : []),
      ...(hasErrors(step2Results) || (isGoalError && !formData?.justification) ? [2] : []),
      ...(hasErrors(step3Results) ? [3] : []),
    ])

    setStepsWithErrors((currentStepsWithErrors) =>
      areNumberSetsEqual(currentStepsWithErrors, nextStepsWithErrors)
        ? currentStepsWithErrors
        : nextStepsWithErrors
    )

    const nextFormValidationErrors = [
      ...step0Results,
      ...step1Results,
      ...step2Results,
      ...step3Results,
    ].filter((it): it is TFormValidationResult => it !== undefined)
    for (const field of [
      'description',
      'hpInvestmentSum',
      'hpInvestmentSource',
      'status',
      'businessPriorities',
    ]) {
      dispatch(
        updateInvalidData({
          key: initiativeFormKey,
          field,
          validationMessage:
            nextFormValidationErrors.find((error) => error.field === field)?.message ?? '',
        })
      )
    }
    setFormValidationErrors((currentFormValidationErrors) =>
      areFormValidationResultsEqual(currentFormValidationErrors, nextFormValidationErrors)
        ? currentFormValidationErrors
        : nextFormValidationErrors
    )

    return nextFormValidationErrors
  }, [
    initiativeFormKey,
    formData,
    keyObjectives.length,
    isInvestmentNeeds,
    t,
    estimateSalesTargetField,
    goalMode,
    initialData?.isFromGlobalPartner,
    isPracticeDevelopment,
    handleDescriptionValidate,
    isGoalError,
    dispatch,
    isBusinessPrioritiesRequired,
    isEditingImportedInitiative,
  ])

  const handleValidateRef = useRef(handleValidate)
  handleValidateRef.current = handleValidate

  const handleApply = useCallback(async () => {
    if (submissionInProgressRef.current) return

    submissionInProgressRef.current = true
    setIsLoading(true)
    onBusyStateChange?.(true)
    try {
      const errors = await handleValidate()
      if (errors.length > 0) return

      const descriptionQuality = isEditingImportedInitiative
        ? formData.descriptionQuality
        : await checkDescriptionQuality({ force: true })
      if (!isEditingImportedInitiative && isDescriptionQualityBlocking(descriptionQuality)) {
        setCurrentStep(0)
        return
      }

      await onApply({ ...formData, descriptionQuality }, isInvestmentNeeds)
      if (pendingKeyInitiative) {
        dispatch(reviewKeyInitiative())
      }
    } finally {
      submissionInProgressRef.current = false
      setIsLoading(false)
      onBusyStateChange?.(false)
    }
  }, [
    handleValidate,
    isEditingImportedInitiative,
    checkDescriptionQuality,
    onApply,
    onBusyStateChange,
    formData,
    isInvestmentNeeds,
    pendingKeyInitiative,
    dispatch,
  ])

  const renderActiveSection = () => {
    switch (currentStep) {
      case 0:
        return (
          <InitiativeSection
            {...props}
            availableBusinessPriorities={availableBusinessPriorities}
            partnerMarket={partnerMarket}
            isBusinessPrioritiesPresent={isBusinessPrioritiesPresent}
            isBusinessPrioritiesRequired={isBusinessPrioritiesRequired}
            isPracticeDevelopment={isPracticeDevelopment}
            onDescriptionQualityCheck={handleDescriptionBlur}
            isDescriptionQualityVisible={isDescriptionQualityValidationActive}
            onDescriptionValidate={handleDescriptionValidate}
          />
        )
      case 1:
        return <FoundingSection {...props} isPracticeDevelopment={isPracticeDevelopment} />
      case 2:
        return (
          <GoalSection
            {...props}
            isPracticeDevelopment={isPracticeDevelopment}
            hasEstimateSalesValidationError={hasEstimateSalesValidationError}
          />
        )
      case 3:
        return <StatusSection {...props} isPracticeDevelopment={isPracticeDevelopment} />
      default:
        return null
    }
  }

  /** -------------------- Effects ----------------------- */

  useEffect(() => {
    onBusyStateChange?.(isBusy)
  }, [isBusy, onBusyStateChange])

  useEffect(() => {
    Logger.log(`[KeyInitiativeEditor] Apply button is ${isApplyDisabled ? 'blocked' : 'enabled'}`, {
      formKey: initiativeFormKey,
      reasons: applyBlockingReasons,
    })
  }, [applyBlockingReasons, initiativeFormKey, isApplyDisabled])

  useEffect(() => {
    // Apply defaults only on SI changes, not on opening or revisiting a tab.
    // Keep this at form level so Goal Settings does not need to be mounted.
    if (previousDefaultsKeyObjectiveRef.current === formData?.keyObjective) return
    previousDefaultsKeyObjectiveRef.current = formData?.keyObjective

    const selectedKeyObjective = keyObjectives.find((item) => item.key === formData?.keyObjective)
    const defaultFocusAreas = parseKeyInitiativeFocusArea(selectedKeyObjective?.defaulFocusArea)

    if (selectedKeyObjective?.defaultBusinessUnit) {
      dispatch(
        updateFormDataField({
          field: 'businessUnit',
          value: selectedKeyObjective.defaultBusinessUnit,
          formKey: initiativeFormKey,
        })
      )
    }
    if (defaultFocusAreas.length > 0) {
      dispatch(
        updateFormDataField({
          field: 'focusArea',
          value: defaultFocusAreas.join(','),
          formKey: initiativeFormKey,
        })
      )
    }
  }, [dispatch, formData?.keyObjective, initiativeFormKey, keyObjectives])

  useEffect(() => {
    // Funding defaults must also apply while only the Initiative tab is mounted.
    if (!isMdfKeyObjective) return

    const shouldPreselectFunding =
      previousKeyObjectiveRef.current !== formData?.keyObjective ||
      formData?.isInvestmentNeeds === undefined

    // Preselect on MDF selection, but respect a subsequently unchecked checkbox.
    if (shouldPreselectFunding && !formData?.isInvestmentNeeds) {
      dispatch(
        updateFormDataField({
          field: 'isInvestmentNeeds',
          value: true,
          formKey: initiativeFormKey,
        })
      )
    }
    if (
      (shouldPreselectFunding || isInvestmentNeeds) &&
      formData?.hpInvestmentSource !== ActionsPlanTypes.MDF
    ) {
      dispatch(
        updateFormDataField({
          field: 'hpInvestmentSource',
          value: ActionsPlanTypes.MDF,
          formKey: initiativeFormKey,
        })
      )
    }
  }, [
    dispatch,
    formData?.hpInvestmentSource,
    formData?.isInvestmentNeeds,
    formData?.keyObjective,
    initiativeFormKey,
    isMdfKeyObjective,
    isInvestmentNeeds,
  ])

  useEffect(() => {
    // Imported sales targets are hidden/read-only even when editable Funding changes.
    if (isEditingImportedInitiative) return

    const currentInvestmentSource = formData?.hpInvestmentSource
    const isMdfSelectionChanged =
      isMdfKeyObjective && previousKeyObjectiveRef.current !== formData?.keyObjective
    const shouldSetIncremental =
      isMdfSelectionChanged ||
      shouldSetIncrementalForFunding({
        previousInvestmentSource: previousInvestmentSourceRef.current,
        currentInvestmentSource,
        previousIsInvestmentNeeds: previousIsInvestmentNeedsRef.current,
        currentIsInvestmentNeeds: isInvestmentNeeds,
        isIncrementalModeLocked: IS_INCREMENTAL_MODE_LOCKED_BY_FUNDING_SOURCE,
      })

    previousInvestmentSourceRef.current = currentInvestmentSource
    previousIsInvestmentNeedsRef.current = isInvestmentNeeds
    previousKeyObjectiveRef.current = formData?.keyObjective

    if (!shouldSetIncremental || formData?.goalMode === EKeyInitiativeGoalMode.Incremental) {
      return
    }

    dispatch(
      updateFormDataField({
        field: incrementalFieldNameByValueType,
        value: runRateEstimateSalesValue,
        formKey: initiativeFormKey,
      })
    )
    dispatch(
      updateFormDataField({
        field: goalFieldName,
        value: null,
        formKey: initiativeFormKey,
      })
    )
    dispatch(
      updateFormDataField({
        field: 'shouldAddToBridges',
        value: true,
        formKey: initiativeFormKey,
      })
    )
    dispatch(
      updateFormDataField({
        field: 'goalMode',
        value: EKeyInitiativeGoalMode.Incremental,
        formKey: initiativeFormKey,
      })
    )
  }, [
    dispatch,
    formData?.hpInvestmentSource,
    formData?.keyObjective,
    formData?.goalMode,
    goalFieldName,
    incrementalFieldNameByValueType,
    initiativeFormKey,
    isInvestmentNeeds,
    isEditingImportedInitiative,
    isMdfKeyObjective,
    runRateEstimateSalesValue,
  ])

  useEffect(() => {
    if (isPracticeDevelopment && currentStep === FOUNDING_STEP_INDEX) {
      setCurrentStep(0)
    }
  }, [isPracticeDevelopment, currentStep])

  useEffect(() => {
    if (isEditingImportedInitiative) {
      setIsGoalError(false)
      return
    }

    if (finSobData) {
      const { isError } = finSobValidation(
        formData,
        finSobData,
        estimateSalesTargetField,
        otherInitiatives
      )
      setIsGoalError(isError)
    }
  }, [
    estimateSalesTargetField,
    finSobData,
    formData,
    otherInitiatives,
    isEditingImportedInitiative,
  ])

  useEffect(() => {
    return () => {
      dispatch(resetAllModifiedDataByKey(initiativeFormKey))
    }
  }, [dispatch, initiativeFormKey])

  useEffect(() => {
    if (
      !shouldValidateOnOpening &&
      modifiedFieldsRef.current.length === 0 &&
      !isDescriptionQualityValidationActive
    ) {
      return
    }
    void handleValidateRef.current()
  }, [
    formData,
    shouldValidateOnOpening,
    isDescriptionQualitySaveBlocked,
    isDescriptionQualityValidationActive,
    isGoalError,
    isMdfKeyObjective,
    isBusinessPrioritiesRequired,
  ])

  useEffect(() => {
    if (!isPracticeDevelopmentMountedRef.current) {
      isPracticeDevelopmentMountedRef.current = true
      return
    }
    handleValidateRef.current()
  }, [isPracticeDevelopment])

  /** -------------------- Render ----------------------- */

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.sidebar}>
          <KeyInitiativeStepsMenu
            steps={menuSteps}
            disabledSteps={disabledSteps}
            stepsWithErrors={stepsWithErrors}
            onSelect={setCurrentStep}
          />
        </div>
        <div className={styles.sectionPanel}>{renderActiveSection()}</div>
      </div>

      <div className={styles.footer}>
        {keyInitiativesFormat < INITIATIVES_EDITOR_TYPE_STEPS && !!setKeyInitiativesFormat && (
          <WidgetView value={keyInitiativesFormat} onOptionClick={setKeyInitiativesFormat} />
        )}
        <div className={styles.buttons}>
          <CancelButton onClick={props.onCancel} color="grey" />
          <ApplyButton
            text={props.saveAsNew ? tr.keyInitiativeEditor.copyAsNewButton : undefined}
            className={styles.applyButton}
            onClick={handleApply}
            disabled={isApplyDisabled}
          />
        </div>
      </div>
    </div>
  )
}
